import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ClipboardList, Package, Send, SendHorizontal, BellOff, Bell, XCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useNotifications } from "@/lib/notifications";
import { Eyebrow, PageTitle, PageSubtitle, Loader, EmptyState, Card } from "@/components/common";

const ICONS = {
  message: MessageSquare,
  mission_assigned: ClipboardList,
  trip_assigned: Package,
  carrier_invite: Send,
  trip_transferred: SendHorizontal,
  trip_cancelled: XCircle,
};

function when(v) {
  return v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshUnread, pushTick } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const data = await apiFetch("/notifications").catch(() => []);
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  }, []);

  // Aggiornamento automatico: al mount, in polling, e quando arriva una push dal Service Worker.
  useEffect(() => { load(); }, [load, pushTick]);
  useEffect(() => {
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const openNotification = (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      apiFetch(`/notifications/${n.id}/read`, { method: "PATCH" }).then(refreshUnread).catch(() => {});
    }
    if (n.deep_link && n.deep_link.startsWith("/") && !n.deep_link.startsWith("/(")) navigate(n.deep_link);
  };

  const markAll = () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    apiFetch("/notifications/read-all", { method: "PATCH" }).then(refreshUnread).catch(() => {});
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Eyebrow>AVVISI</Eyebrow>
          <PageTitle>Notifiche</PageTitle>
          <PageSubtitle>Avvisi essenziali su missioni, messaggi e aggiornamenti operativi.</PageSubtitle>
        </div>
        {unread > 0 ? (
          <button data-testid="mark-all-read" onClick={markAll} className="shrink-0 rounded-xl bg-secondary px-3 py-2 text-xs font-extrabold text-secondary-foreground">
            Segna tutte lette ({unread})
          </button>
        ) : null}
      </div>
      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState icon={BellOff} title="Nessuna notifica" text="Gli avvisi importanti appariranno qui." />
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const Icon = ICONS[it.type] || Bell;
            return (
              <Card
                key={it.id}
                data-testid={`notification-${it.id}`}
                onClick={() => openNotification(it)}
                className={`flex cursor-pointer items-start gap-3 ${it.read ? "" : "ring-1 ring-primary/30"}`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary">
                  <Icon className="h-5 w-5 text-primary" />
                </span>
                <div className="flex-1">
                  <p className="text-sm font-extrabold text-foreground">
                    {it.title}
                    {!it.read ? <span className="ml-2 inline-block h-2 w-2 rounded-full bg-primary align-middle" /> : null}
                  </p>
                  <p className="mt-0.5 text-[13px] leading-snug text-card-foreground">{it.message}</p>
                  {it.created_at ? <p className="mt-1 text-[11px] text-muted-foreground">{when(it.created_at)}</p> : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
